import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Building2, Mail, Phone, Globe, MapPin, FileText, Calendar, 
  MessageSquare, CheckCircle, XCircle, Clock, ArrowRight, 
  Package, Gift, Handshake, Sparkles, Send,
  Plus, Eye, Trash2, RefreshCw, Paperclip, Download, ExternalLink
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const requestTypeLabels = {
  reduction: { label: "Réduction adhérents", icon: Gift, color: "bg-green-100 text-green-700" },
  partner_offer: { label: "Offre partenaire", icon: Handshake, color: "bg-blue-100 text-blue-700" },
  product_test: { label: "Produit à tester", icon: Package, color: "bg-purple-100 text-purple-700" },
  other: { label: "Autre collaboration", icon: Sparkles, color: "bg-orange-100 text-orange-700" },
};

const statusLabels = {
  nouveau: { label: "Nouveau", color: "bg-blue-100 text-blue-700", icon: Clock },
  a_etudier: { label: "À étudier", color: "bg-yellow-100 text-yellow-700", icon: Eye },
  contacte: { label: "Contacté", color: "bg-purple-100 text-purple-700", icon: MessageSquare },
  accepte: { label: "Accepté", color: "bg-green-100 text-green-700", icon: CheckCircle },
  refuse: { label: "Refusé", color: "bg-red-100 text-red-700", icon: XCircle },
};

export default function PartnerRequestsManagementPage() {
  const { token } = useAuth();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showIntegrateModal, setShowIntegrateModal] = useState(false);
  
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  
  const [responseMessage, setResponseMessage] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  
  const [integrateOptions, setIntegrateOptions] = useState({
    create_partner: true,
    create_deal: false,
    create_review: false,
  });

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterType]);

  const loadRequests = async () => {
    try {
      let url = `${API}/api/partner-requests`;
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (filterType !== "all") params.append("request_type", filterType);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data);
    } catch (error) {
      console.error("Error loading requests:", error);
      toast.error("Impossible de charger les demandes");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      await axios.put(
        `${API}/api/partner-requests/${requestId}?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Demande passée en "${statusLabels[newStatus].label}"`);
      loadRequests();
      
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Impossible de mettre à jour le statut");
    }
  };

  const saveAdminNotes = async () => {
    if (!selectedRequest) return;
    
    try {
      await axios.put(
        `${API}/api/partner-requests/${selectedRequest.id}?admin_notes=${encodeURIComponent(adminNotes)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Notes enregistrées");
      setSelectedRequest({ ...selectedRequest, admin_notes: adminNotes });
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Impossible d'enregistrer les notes");
    }
  };

  const sendResponse = async () => {
    if (!selectedRequest || !responseMessage) return;
    
    try {
      await axios.post(
        `${API}/api/partner-requests/${selectedRequest.id}/respond?message=${encodeURIComponent(responseMessage)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Réponse enregistrée. Email à envoyer manuellement.");
      
      setShowResponseModal(false);
      setResponseMessage("");
      loadRequests();
    } catch (error) {
      console.error("Error sending response:", error);
      toast.error("Impossible d'envoyer la réponse");
    }
  };

  const integrateRequest = async () => {
    if (!selectedRequest) return;
    
    try {
      const params = new URLSearchParams();
      params.append("create_partner", integrateOptions.create_partner);
      params.append("create_deal", integrateOptions.create_deal);
      params.append("create_review", integrateOptions.create_review);
      
      const res = await axios.post(
        `${API}/api/partner-requests/${selectedRequest.id}/integrate?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Intégration effectuée. Éléments en brouillon.");
      
      setShowIntegrateModal(false);
      loadRequests();
    } catch (error) {
      console.error("Error integrating:", error);
      toast.error(error.response?.data?.detail || "Impossible d'intégrer la demande");
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    
    try {
      await axios.delete(`${API}/api/partner-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Demande supprimée");
      loadRequests();
      setShowDetailModal(false);
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Impossible de supprimer");
    }
  };

  const openDetail = (request) => {
    setSelectedRequest(request);
    setAdminNotes(request.admin_notes || "");
    setShowDetailModal(true);
  };

  const getTypeBadge = (type) => {
    const config = requestTypeLabels[type] || requestTypeLabels.other;
    return (
      <Badge className={`${config.color} gap-1`}>
        <config.icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const config = statusLabels[status] || statusLabels.nouveau;
    return (
      <Badge className={`${config.color} gap-1`}>
        <config.icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const stats = {
    total: requests.length,
    nouveau: requests.filter(r => r.status === "nouveau").length,
    a_etudier: requests.filter(r => r.status === "a_etudier").length,
    accepte: requests.filter(r => r.status === "accepte").length,
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="partner-requests-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
              <Building2 className="h-6 w-6" />
              Demandes Entreprises
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérez les propositions de partenariat
            </p>
          </div>
          <Button onClick={loadRequests} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-primary">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{stats.nouveau}</p>
              <p className="text-sm text-blue-600">Nouvelles</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 bg-yellow-50/50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-700">{stats.a_etudier}</p>
              <p className="text-sm text-yellow-600">À étudier</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{stats.accepte}</p>
              <p className="text-sm text-green-600">Acceptées</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(statusLabels).map(([value, config]) => (
                <SelectItem key={value} value={value}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {Object.entries(requestTypeLabels).map(([value, config]) => (
                <SelectItem key={value} value={value}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : requests.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune demande trouvée</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {getTypeBadge(request.request_type)}
                        {getStatusBadge(request.status)}
                        {request.response_sent && (
                          <Badge variant="outline" className="gap-1">
                            <Send className="h-3 w-3" />
                            Répondu
                          </Badge>
                        )}
                        {request.created_partner_id && (
                          <Badge variant="outline" className="gap-1 bg-green-50">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                            Intégré
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-lg text-primary">{request.company_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {request.contact_name} {request.contact_function && `• ${request.contact_function}`}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1 flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {request.email}
                        </span>
                        {request.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {request.phone}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(request.created_at).toLocaleDateString('fr-FR', { 
                            day: 'numeric', month: 'long', year: 'numeric' 
                          })}
                        </span>
                        {request.attached_documents && request.attached_documents.length > 0 && (
                          <span className="flex items-center gap-1 text-accent">
                            <Paperclip className="h-3 w-3" />
                            {request.attached_documents.length} fichier{request.attached_documents.length > 1 ? "s" : ""}
                          </span>
                        )}
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => openDetail(request)}>
                        <Eye className="h-4 w-4 mr-1" />
                        Voir
                      </Button>
                      
                      {request.status === "nouveau" && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateStatus(request.id, "a_etudier")}
                        >
                          Étudier
                        </Button>
                      )}
                      
                      {request.status === "a_etudier" && (
                        <>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => updateStatus(request.id, "accepte")}
                          >
                            Accepter
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => updateStatus(request.id, "refuse")}
                          >
                            Refuser
                          </Button>
                        </>
                      )}
                      
                      {request.status === "accepte" && !request.created_partner_id && (
                        <Button 
                          size="sm"
                          className="bg-accent hover:bg-accent/90"
                          onClick={() => {
                            setSelectedRequest(request);
                            setIntegrateOptions({
                              create_partner: true,
                              create_deal: request.request_type === "reduction" || request.request_type === "partner_offer",
                              create_review: request.request_type === "product_test",
                            });
                            setShowIntegrateModal(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Intégrer
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedRequest && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  {getTypeBadge(selectedRequest.request_type)}
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <DialogTitle>{selectedRequest.company_name}</DialogTitle>
                <DialogDescription>
                  Demande reçue le {new Date(selectedRequest.created_at).toLocaleDateString('fr-FR')}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Contact</p>
                    <p className="font-medium">{selectedRequest.contact_name}</p>
                    {selectedRequest.contact_function && (
                      <p className="text-sm text-muted-foreground">{selectedRequest.contact_function}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Coordonnées</p>
                    <p className="text-sm flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      <a href={`mailto:${selectedRequest.email}`} className="text-accent hover:underline">
                        {selectedRequest.email}
                      </a>
                    </p>
                    {selectedRequest.phone && (
                      <p className="text-sm flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {selectedRequest.phone}
                      </p>
                    )}
                    {selectedRequest.website && (
                      <p className="text-sm flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        <a href={selectedRequest.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                          Site web
                        </a>
                      </p>
                    )}
                  </div>
                </div>

                {(selectedRequest.address_street || selectedRequest.address_city) && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Adresse</p>
                    <p className="text-sm">
                      {selectedRequest.address_street}<br />
                      {selectedRequest.address_postal_code} {selectedRequest.address_city}<br />
                      {selectedRequest.address_country}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Proposition</p>
                  <p className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                    {selectedRequest.description}
                  </p>
                </div>

                {selectedRequest.conditions && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Conditions</p>
                    <p className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                      {selectedRequest.conditions}
                    </p>
                  </div>
                )}

                {/* Pièces jointes */}
                {selectedRequest.attached_documents && selectedRequest.attached_documents.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-2 flex items-center gap-2">
                      <Paperclip className="h-3 w-3" />
                      Pièces jointes ({selectedRequest.attached_documents.length})
                    </p>
                    <div className="space-y-2">
                      {selectedRequest.attached_documents.map((url, index) => {
                        const metadata = selectedRequest.attached_documents_metadata?.[index];
                        const fileName = metadata?.file_name || `Document ${index + 1}`;
                        const fileSize = metadata?.file_size;
                        const fileType = metadata?.file_type || "";
                        
                        const formatSize = (bytes) => {
                          if (!bytes) return "";
                          if (bytes < 1024) return bytes + " o";
                          if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " Ko";
                          return (bytes / (1024 * 1024)).toFixed(1) + " Mo";
                        };

                        const isImage = fileType.includes("jpg") || fileType.includes("jpeg") || fileType.includes("png");
                        
                        return (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{fileName}</p>
                                <p className="text-xs text-muted-foreground">
                                  {fileType.replace(".", "").toUpperCase()}
                                  {fileSize && ` • ${formatSize(fileSize)}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isImage && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(url, "_blank")}
                                  className="h-8 w-8 p-0"
                                  title="Aperçu"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <a 
                                href={url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                download={fileName}
                              >
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-3"
                                >
                                  <Download className="h-4 w-4 mr-1" />
                                  Télécharger
                                </Button>
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Notes internes</p>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Notes privées pour le suivi..."
                    rows={3}
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={saveAdminNotes}
                  >
                    Enregistrer les notes
                  </Button>
                </div>

                {(selectedRequest.created_partner_id || selectedRequest.created_deal_id || selectedRequest.created_review_id) && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-xs text-green-700 uppercase font-medium mb-2">Éléments créés</p>
                    <div className="space-y-1 text-sm">
                      {selectedRequest.created_partner_id && (
                        <p className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Fiche partenaire créée
                        </p>
                      )}
                      {selectedRequest.created_deal_id && (
                        <p className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Bon plan créé (brouillon)
                        </p>
                      )}
                      {selectedRequest.created_review_id && (
                        <p className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Test produit créé (brouillon)
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDetailModal(false);
                    setShowResponseModal(true);
                  }}
                >
                  <Send className="h-4 w-4 mr-1" />
                  Répondre
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteRequest(selectedRequest.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Supprimer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Response Modal */}
      <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Répondre à {selectedRequest?.company_name}</DialogTitle>
            <DialogDescription>
              Votre réponse sera enregistrée avec les coordonnées ITERCATUS.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Textarea
              value={responseMessage}
              onChange={(e) => setResponseMessage(e.target.value)}
              placeholder="Votre message..."
              rows={6}
            />
            
            <div className="bg-gray-50 p-3 rounded-lg text-xs text-muted-foreground">
              <p className="font-medium mb-1">Signature automatique :</p>
              <p>
                ITERCATUS<br />
                Attention Gilles Fouque<br />
                Quartier les 3 Fonds<br />
                13124 Peypin
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResponseModal(false)}>
              Annuler
            </Button>
            <Button onClick={sendResponse} disabled={!responseMessage}>
              <Send className="h-4 w-4 mr-1" />
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Integrate Modal */}
      <Dialog open={showIntegrateModal} onOpenChange={setShowIntegrateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Intégrer la demande</DialogTitle>
            <DialogDescription>
              Créer automatiquement les éléments dans le site (en brouillon)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={integrateOptions.create_partner}
                  onChange={(e) => setIntegrateOptions({ ...integrateOptions, create_partner: e.target.checked })}
                  className="w-4 h-4"
                />
                <div>
                  <p className="font-medium">Créer la fiche partenaire</p>
                  <p className="text-xs text-muted-foreground">Ajouter l'entreprise dans la liste des partenaires</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={integrateOptions.create_deal}
                  onChange={(e) => setIntegrateOptions({ ...integrateOptions, create_deal: e.target.checked })}
                  className="w-4 h-4"
                />
                <div>
                  <p className="font-medium">Créer un bon plan</p>
                  <p className="text-xs text-muted-foreground">Créer une offre/avantage en brouillon</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={integrateOptions.create_review}
                  onChange={(e) => setIntegrateOptions({ ...integrateOptions, create_review: e.target.checked })}
                  className="w-4 h-4"
                />
                <div>
                  <p className="font-medium">Créer un test produit</p>
                  <p className="text-xs text-muted-foreground">Créer une fiche test en brouillon</p>
                </div>
              </label>
            </div>
            
            <p className="text-xs text-muted-foreground bg-yellow-50 p-3 rounded-lg">
              Les éléments seront créés en brouillon. Vous pourrez les compléter et les publier manuellement.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIntegrateModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={integrateRequest}
              className="bg-accent hover:bg-accent/90"
            >
              <Plus className="h-4 w-4 mr-1" />
              Créer les éléments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
